import React, { useCallback, useRef, useState } from 'react';
import { DateRange } from '@cyberlz/react-date-range';
import type { RangeKeyDict } from '@cyberlz/react-date-range';
import { es } from 'date-fns/locale/es';
import { useDismissable } from './useDismissable';
import { formatShortDate, startOfToday } from './dateValues';

export interface DateRangeValue {
  startDate: Date | null;
  endDate: Date | null;
}

interface DateRangeFieldProps {
  id: string;
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  /** Single-date mode (one-way flights): the end date mirrors the start. */
  isSingleDate?: boolean;
  startPlaceholder?: string;
  endPlaceholder?: string;
  minDate?: Date;
  disabled?: boolean;
  hasError?: boolean;
  errorId?: string;
  triggerLabel?: string;
}

/**
 * Date range picker for the hero search card, shared by flights and hotels.
 *
 * Wraps the `@cyberlz/react-date-range` calendar the site already ships (and
 * already styles globally) with this feature's own trigger and popover, so the
 * closed state reads exactly like the other search-card fields.
 */
export const DateRangeField: React.FC<DateRangeFieldProps> = ({
  disabled = false,
  endPlaceholder = 'Vuelta',
  errorId,
  hasError = false,
  id,
  isSingleDate = false,
  minDate,
  onChange,
  startPlaceholder = 'Ida',
  triggerLabel = 'Fechas',
  value,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);
  useDismissable(isOpen, containerRef, close);

  const today = startOfToday();
  const fallback = value.startDate || today;

  const handleChange = (ranges: RangeKeyDict) => {
    const { endDate, startDate } = ranges.selection ?? {};
    const nextStart = startDate ?? null;
    const nextEnd = isSingleDate ? nextStart : (endDate ?? null);

    onChange({ endDate: nextEnd, startDate: nextStart });

    // A range needs both ends before the panel is worth closing; a single date
    // is complete as soon as it is picked.
    if (isSingleDate) {
      close();
      return;
    }

    if (nextStart && nextEnd && nextEnd.getTime() !== nextStart.getTime()) {
      close();
    }
  };

  const startText = formatShortDate(value.startDate);
  const endText = formatShortDate(value.endDate);

  return (
    <div className='searcher-field__anchor' ref={containerRef}>
      <button
        aria-describedby={hasError && errorId ? errorId : undefined}
        aria-expanded={isOpen}
        aria-haspopup='dialog'
        aria-label={triggerLabel}
        className={['searcher-field__control', hasError && 'searcher-field__control--invalid']
          .filter(Boolean)
          .join(' ')}
        disabled={disabled}
        id={id}
        onClick={() => setIsOpen((open) => !open)}
        type='button'
      >
        <span
          className={[
            'searcher-field__control-text',
            !startText && 'searcher-field__control-text--placeholder',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {startText || startPlaceholder}
        </span>
        {!isSingleDate && (
          <>
            <span aria-hidden='true' className='searcher-field__control-separator'>
              →
            </span>
            <span
              className={[
                'searcher-field__control-text',
                !endText && 'searcher-field__control-text--placeholder',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {endText || endPlaceholder}
            </span>
          </>
        )}
      </button>

      {isOpen && (
        <div
          aria-label={triggerLabel}
          className='searcher-popover searcher-popover--calendar'
          role='dialog'
        >
          <DateRange
            direction='horizontal'
            locale={es}
            minDate={minDate ?? today}
            months={2}
            onChange={handleChange}
            rangeColors={['#1976d2']}
            ranges={[
              {
                endDate: isSingleDate ? fallback : value.endDate || fallback,
                key: 'selection',
                startDate: fallback,
              },
            ]}
            retainEndDateOnFirstSelection={false}
            showDateDisplay={false}
            showMonthAndYearPickers={false}
          />
        </div>
      )}
    </div>
  );
};
